import { useRef, useState } from "react";
import { Banner, Button } from "@/components/ui";
import { CloudUpload, Check, Plus, X } from "lucide-react";
import { StepFooter } from "./StepFooter";
import { validateTechRiderFile } from "./validators";
import { type InquiryProfileConfig } from "./inquiry-profile-schema";
import styles from "./TechRiderStep.module.css";

interface TechRiderStepProps {
  profile: InquiryProfileConfig;
  file: File | null;
  fileError: string | null;
  parseFailed: boolean;
  onFileChange: (file: File | null) => void;
  /** Extra attachments beyond the parsed primary document (#168). */
  additionalFiles?: File[];
  onAddAdditionalFiles?: (files: File[]) => void;
  onRemoveAdditionalFile?: (index: number) => void;
  onCancel: () => void;
  backLabel?: string;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled: boolean;
}

export function TechRiderStep({
  profile,
  file,
  fileError,
  parseFailed,
  onFileChange,
  additionalFiles = [],
  onAddAdditionalFiles,
  onRemoveAdditionalFile,
  onCancel,
  backLabel = "Cancel",
  onContinue,
  continueLabel,
  continueDisabled,
}: TechRiderStepProps): React.JSX.Element {
  const copy = profile.documentsStep;
  const [isDragging, setIsDragging] = useState(false);
  const [localFileError, setLocalFileError] = useState<string | null>(null);
  const [additionalError, setAdditionalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalInputRef = useRef<HTMLInputElement>(null);

  const clearFileInput = (): void => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openFilePicker = (): void => {
    // Let the same file be selected again after removal/replacement.
    clearFileInput();
    fileInputRef.current?.click();
  };

  const handleFile = (next: File | null): void => {
    setLocalFileError(null);
    if (!next) clearFileInput();
    onFileChange(next);
  };

  const handleFileInput = (files: FileList | null): void => {
    const next = files?.[0];
    if (!next) return;
    // Single validator covers both extension + size — same rule the hook
    // checks, so a file accepted here can't be silently rejected later.
    const validation = validateTechRiderFile(next);
    if (!validation.ok) {
      setLocalFileError(validation.message);
      clearFileInput();
      return;
    }
    handleFile(next);
  };

  const openAdditionalPicker = (): void => {
    if (additionalInputRef.current) additionalInputRef.current.value = "";
    additionalInputRef.current?.click();
  };

  const handleAdditionalInput = (files: FileList | null): void => {
    if (!files || files.length === 0) return;
    setAdditionalError(null);
    const accepted: File[] = [];
    for (const next of Array.from(files)) {
      const validation = validateTechRiderFile(next);
      if (!validation.ok) {
        setAdditionalError(validation.message);
        continue;
      }
      accepted.push(next);
    }
    if (accepted.length > 0) onAddAdditionalFiles?.(accepted);
  };

  const shownError = localFileError ?? fileError;

  return (
    <div className={styles.step}>
      <div>
        <h1 className={styles.title}>{copy.title}</h1>
        <p className={styles.description}>{copy.description}</p>

        <div
          role="button"
          tabIndex={0}
          aria-label={copy.uploadAriaLabel}
          className={`${styles.dropzone} ${isDragging ? styles.dragging : ""}`}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
            handleFileInput(e.dataTransfer.files);
          }}
          onClick={openFilePicker}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openFilePicker();
            }
          }}
        >
          <div className={styles.uploadBody}>
            <div className={styles.uploadIcon}>
              <CloudUpload className={styles.uploadIconGlyph} />
            </div>
            <p className={styles.uploadPrompt}>
              {isDragging ? "Drop your file here" : copy.uploadPrompt}
            </p>
            <p className={styles.uploadFormats}>{copy.acceptedFormatsLabel}</p>
            <input
              ref={fileInputRef}
              id="tech-rider-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => handleFileInput(e.target.files)}
              className={styles.hiddenInput}
            />
          </div>
          {file && (
            <div
              className={styles.primaryFileRow}
              onClick={(e) => e.stopPropagation()}
            >
              <p className={styles.fileName}>
                <Check className={styles.fileCheck} />
                {file.name}
              </p>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove file"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFile(null);
                }}
              >
                <X className={styles.removeIcon} />
              </Button>
            </div>
          )}
        </div>

        {shownError && (
          <p role="alert" className={styles.error}>
            {shownError}
          </p>
        )}

        {file && parseFailed && !shownError && (
          <Banner
            variant="warning"
            className={styles.parseWarning}
            title={copy.parseFailedTitle}
          >
            <p>{copy.parseFailedMessage}</p>
          </Banner>
        )}

        {onAddAdditionalFiles && (
          <div className={styles.extras}>
            {additionalFiles.length > 0 && (
              <div className={styles.extraFiles}>
                {additionalFiles.map((doc, index) => (
                  <div key={`${doc.name}-${index}`} className={styles.fileRow}>
                    <p className={styles.fileName}>
                      <Check className={styles.fileCheck} />
                      {doc.name}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${doc.name}`}
                      onClick={() => onRemoveAdditionalFile?.(index)}
                    >
                      <X className={styles.removeIcon} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={openAdditionalPicker}
            >
              <Plus className={styles.addIcon} />
              Add another document
            </Button>
            <input
              ref={additionalInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx"
              aria-label="Add more documents"
              onChange={(e) => handleAdditionalInput(e.target.files)}
              className={styles.hiddenInput}
            />
            {additionalError && (
              <p role="alert" className={styles.error}>
                {additionalError}
              </p>
            )}
          </div>
        )}
      </div>

      <StepFooter
        onBack={onCancel}
        backLabel={backLabel}
        onContinue={onContinue}
        continueLabel={continueLabel}
        disabled={continueDisabled}
      />
    </div>
  );
}
